'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { SchemaFAQ } from '../foundation/schema';
import { Accordion } from './accordion';
import { SmartImage } from './smartimage';
import './faq.css';
const categoryIcons = {
    'Getting Started': '🚀',
    'Process & Timeline': '⏱️',
    'Technical Details': '⚙️',
    'Content & Management': '📝',
    'Support & Maintenance': '🛠️',
    'Ownership & Legal': '📋',
    'Services': '💼',
    '': ''
};
/**
 * FAQ — list of frequently asked questions rendered as an accordion with search and expand controls.
 *
 * @param {shape} [props.faqsData] - Object containing FAQ data; expected to include a `mainEntity` array of question objects.
 * @param {arrayOf} [props.mainEntity] - Array of FAQ objects each with `name`, `category`, and `acceptedAnswer`.
 * @param {string} [props.name] - Question title text.
 * @param {string} [props.category] - Category/section for the FAQ (used for grouping and icons).
 * @param {shape} [props.acceptedAnswer] - Object with an answer, typically containing `text` as string or array of strings.
 * @param {oneOfType} [props.text] - Answer text or an array of paragraphs.
 */
FAQ.propTypes = {
    /** FAQ data wrapper object; should contain mainEntity array. */
    faqsData: PropTypes.shape({
        /** Array of FAQ objects to render as accordion items. */
        mainEntity: PropTypes.arrayOf(PropTypes.shape({
            /** Question text/title. */
            name: PropTypes.string,
            /** Category key for grouping or icons. */
            category: PropTypes.string,
            /** Accepted answer object with text or list of paragraphs. */
            acceptedAnswer: PropTypes.shape({
                image: PropTypes.shape({
                    contentUrl: PropTypes.string,
                    name: PropTypes.string,
                    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
                    layout: PropTypes.oneOf(['left', 'right']),
                }),
                text: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.arrayOf(PropTypes.string)
                ]),
            }),
        })),
    }).isRequired,
};
export function FAQ({ faqsData }) {
    const safeFaqsData = faqsData || { mainEntity: [] };
    const faqEntities = safeFaqsData.mainEntity || [];
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedStates, setExpandedStates] = useState(faqEntities.map(() => false));
    const filteredFaqs = useMemo(() => {
        if (!faqEntities)
            return [];
        if (!searchTerm)
            return faqEntities;
        return faqEntities.filter((faq) => {
            const answerText = Array.isArray(faq.acceptedAnswer?.text)
                ? faq.acceptedAnswer.text.join(' ')
                : faq.acceptedAnswer?.text || '';
            return faq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                answerText.toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [faqEntities, searchTerm]);
    const expandAll = () => {
        setExpandedStates(expandedStates.map(() => true));
    };
    const collapseAll = () => {
        setExpandedStates(expandedStates.map(() => false));
    };
    const handleToggle = (index, open) => {
        setExpandedStates(prev => prev.map((state, i) => i === index ? open : state));
    };
    // Transform FAQ data to Accordion format
    const accordionItems = filteredFaqs.map((faq, index) => {
        const answerImage = faq.acceptedAnswer?.image;
        const imageLayout = answerImage?.layout === 'right' ? 'right' : 'left';
        /* const imageStyle: React.CSSProperties = {
            float: imageLayout,
            width: answerImage?.width ? (typeof answerImage.width === 'number' ? `${answerImage.width}px` : answerImage.width) : undefined,
            height: answerImage?.height ? (typeof answerImage.height === 'number' ? `${answerImage.height}px` : answerImage.height) : undefined,
            margin: imageLayout === 'right' ? '0 0 1rem 1rem' : '0 1rem 1rem 0',
        }; */
        const imageElement = answerImage?.contentUrl ? (_jsx(SmartImage, { variant: "img", src: answerImage.contentUrl, alt: answerImage.name || faq.name, width: typeof answerImage.width === 'number' ? answerImage.width : undefined, height: typeof answerImage.height === 'number' ? answerImage.height : undefined, 
            /* style={imageStyle} */
            //eslint-disable-next-line 
            className: `faq-answer-image faq-answer-image-${imageLayout}` })) : null;
        const answerText = Array.isArray(faq.acceptedAnswer.text) ? (_jsx("div", { children: faq.acceptedAnswer.text.map((paragraph, pIndex) => (_jsx("p", { dangerouslySetInnerHTML: { __html: paragraph } }, pIndex))) })) : (_jsx("div", { dangerouslySetInnerHTML: { __html: faq.acceptedAnswer.text } }));
        const content = (_jsxs("div", { className: "faq-answer", children: [imageElement, _jsx("div", { className: "faq-answer-text", children: answerText })] }));
        return {
            /* title: `${categoryIcons[faq.category as CategoryKey] || '❓'} ${faq.name}`, */
            title: `${categoryIcons[faq.category] || ''} ${faq.name}`,
            content,
            open: expandedStates[index] || undefined
        };
    });
    return (_jsxs("div", { className: "faq-container", role: "region", "aria-label": "Frequently Asked Questions", children: [_jsx(SchemaFAQ, { faqsData: faqsData }), _jsxs("div", { className: "faq-toolbar", children: [_jsxs("div", { className: "search-box", children: [_jsx("input", { type: "text", placeholder: "Search FAQs...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), className: "search-input", "aria-describedby": "search-help" }), _jsx("div", { id: "search-help", className: "sr-only", children: "Search through frequently asked questions by typing keywords" })] }), _jsxs("div", { className: "expand-buttons", children: [_jsx("button", { onClick: expandAll, className: "expand-button expand-all", "aria-label": "Expand all FAQ answers", title: "Expand all answers", children: "+" }), _jsx("button", { onClick: collapseAll, className: "expand-button collapse-all", "aria-label": "Collapse all FAQ answers", title: "Collapse all answers", children: "-" })] })] }), _jsx("div", { className: "faq-list", "aria-live": "polite", "aria-atomic": "false", children: _jsx(Accordion, { items: accordionItems, onToggle: handleToggle }) })] }));
}
